import React, { useState, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus } from 'lucide-react';

// Import the default export which includes VariableProvider
import LayoutComponent from '../pages/node_page2';
// Import PreviewBox separately if exported, or recreate it here
import { useVariables } from '../contexts/VariableContext';
import { resolveVariables } from '../utils/variableResolver';
import {config} from '../config';
import { TextIcon } from 'lucide-react';

// PreviewBox component at node level
const PreviewBox = ({ value, fieldType = "note", nodeId }) => {
  const { variableDict } = useVariables();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const typeColors = config.fieldTypes[fieldType] || config.fieldTypes.note;
  
  React.useEffect(() => {
    if (value) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [value]);
  
  if (!shouldRender) return null;
  
  let displayValue = '';
  let icon = null;

  if (fieldType === "note") {
    displayValue = '';
    icon = <TextIcon />;
  }
  
  if (fieldType === "variable") {
    const resolvedValue = resolveVariables(value, variableDict);
    const stringValue = String(resolvedValue);
    displayValue = stringValue.length > 10 ? stringValue.slice(0, 10) + ' ...' : stringValue;
  }
  
  if (fieldType === "calculation") {
    try {
      const resolvedValue = resolveVariables(value, variableDict);
      const result = eval(resolvedValue);
      const stringResult = String(result);
      displayValue = stringResult.length > 10 ? stringResult.slice(0, 10) + ' ...' : stringResult;
    } catch (error) {
      displayValue = "Error";
    }
  }
  
  return (
    <div 
      className={`flex flex-col justify-center absolute top-0 left-full h-14 min-w-14 ml-3 rounded-lg px-4 py-2 shadow-lg whitespace-nowrap transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        zIndex: 9999,
        backgroundColor: config.colors.fieldBg,
        border: `5px solid ${typeColors.border}`,
        color: config.colors.textPrimary
      }}
    >
      {icon}
      <div className="font-medium">{displayValue}</div>
    </div>
  );
};

// Custom Node that wraps LayoutComponent (which already has VariableProvider)
function LayoutNode({ data }) {
  return (
    <div className="relative bg-white rounded-lg shadow-xl border-2 border-gray-300">
      <Handle type="target" position={Position.Top} />
      
      <div className="p-2">
        <div className="text-xs font-semibold text-gray-600 mb-2 text-center draggable-header">
          {data.label}
        </div>
        <div className="w-[400px] h-[700px] overflow-hidden nodrag">
          <LayoutComponent />
        </div>
      </div>
      
      {/* PreviewBox will render here if needed */}
      {data.previewValue && (
        <PreviewBox 
          value={data.previewValue}
          fieldType={data.previewType}
          nodeId={data.id}
        />
      )}
      
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

// Define custom node types
const nodeTypes = {
  layoutNode: LayoutNode
};

// Initial nodes
const initialNodes = [
  {
    id: '1',
    type: 'layoutNode',
    position: { x: 250, y: 100 },
    data: { label: 'Variable Layout' }
  }
];

const initialEdges = [];

export default function NodePage() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeId, setNodeId] = useState(2);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const addNewNode = useCallback(() => {
    const newNode = {
      id: `${nodeId}`,
      type: 'layoutNode',
      position: { 
        x: Math.random() * 500 + 100, 
        y: Math.random() * 300 + 100 
      },
      data: { label: `Layout ${nodeId}` }
    };
    setNodes((nds) => [...nds, newNode]);
    setNodeId((id) => id + 1);
  }, [nodeId, setNodes]);

  return (
    <div className="w-screen h-screen">
      {/* Add Node Button */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={addNewNode}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Layout Node
        </button>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        className="bg-gray-50"
      >
        <Background color="#aaa" gap={16} />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
}